import { Outlet, NavLink, Navigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { 
  Users, 
  Briefcase, 
  ListFilter, 
  FileText, 
  Settings,
  ChevronLeft,
  Menu,
  LogOut,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import logoImage from "@/assets/logo-corre-digital.png";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { useLogout } from "@/hooks/useLogout";
import { useNavigate } from "react-router-dom";

const navItems = [
  { to: "/empresa", icon: Users, label: "Vitrine de Talentos", end: true },
  { to: "/empresa/vagas", icon: Briefcase, label: "Minhas Vagas" },
  { to: "/empresa/shortlists", icon: ListFilter, label: "Shortlists" },
  { to: "/empresa/relatorios", icon: FileText, label: "Relatórios" },
  { to: "/empresa/configuracoes", icon: Settings, label: "Configurações" },
];

export function EmpresaLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: profileLoading } = useProfile();
  const { org } = usePartnerContext();
  const handleLogout = useLogout("empresa");

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/empresa/login" replace />;
  }

  if (profile && !profile.terms_accepted_at) {
    return <Navigate to="/empresa/termos" replace />;
  }

  const orgName = (org as any)?.name ?? "Empresa Parceira";
  const orgEmail = (org as any)?.contact_email ?? user?.email ?? "";

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full bg-card border-r border-border transition-all duration-300 z-40",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            {!collapsed && (
              <img 
                src={logoImage} 
                alt="Corre Digital" 
                className="h-8 object-contain"
              />
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setCollapsed(!collapsed)}
              className="ml-auto"
            >
              {collapsed ? <Menu className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 py-4 px-2">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-200",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                      )
                    }
                  >
                    <item.icon className="w-5 h-5 shrink-0" />
                    {!collapsed && (
                      <span className="text-sm font-medium">{item.label}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Logout */}
          <div className="px-2 pb-2">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-destructive hover:bg-destructive/5 transition-colors w-full"
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {!collapsed && <span className="text-sm font-medium">Sair</span>}
            </button>
          </div>

          {/* Footer */}
          {!collapsed && (
            <div className="p-4 border-t border-border">
              <div className="text-xs text-muted-foreground">
                <p className="font-medium truncate">{orgName}</p>
                <p className="truncate">{orgEmail}</p>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          "flex-1 transition-all duration-300",
          collapsed ? "ml-16" : "ml-64"
        )}
      >
        <Outlet />
      </main>
    </div>
  );
}
